import { useState, useMemo, useEffect } from 'react';
import { Copy, Check, Eye, Code, Palette, Type, Layout, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui';
import { pageStyles, cardStyles } from '../lib/styles';

interface WidgetConfig {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: string;
  title: string;
  subtitle: string;
  buttonText: string;
  showLogo: boolean;
  logoUrl: string;
  logoHeight: string;
  hideHeader: boolean;
  showProgress: boolean;
  requestPhotosAfterVerification: boolean;
  width: string;
  maxWidth: string;
  height: string;
  variant: 'flat' | 'card';
  language: 'es' | 'en';
}

const DEFAULT_CONFIG: WidgetConfig = {
  primaryColor: '#2563eb',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  borderRadius: '12',
  title: 'Reservar Cita',
  subtitle: 'Seleccione el servicio y horario que mejor le convenga',
  buttonText: 'Confirmar Reserva',
  showLogo: true,
  logoUrl: '',
  logoHeight: '40',
  hideHeader: false,
  showProgress: true,
  requestPhotosAfterVerification: false,
  width: '100%',
  maxWidth: '600px',
  height: '600',
  variant: 'flat',
  language: 'es',
};

export function EmbedConfigPage() {
  const { success: showSuccess, error: showError } = useToast();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<WidgetConfig>(DEFAULT_CONFIG);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'code'>('preview');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch widget config from API
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'settings', 'widgetConfig'],
    queryFn: () => api.get<{ key: string; value: WidgetConfig; isDefault?: boolean }>(
      '/api/admin/settings/widgetConfig'
    ),
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (value: WidgetConfig) =>
      api.put<{ key: string; value: WidgetConfig }>('/api/admin/settings/widgetConfig', { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'widgetConfig'] });
      setHasChanges(false);
      showSuccess('Configuracion guardada');
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Error al guardar');
    },
  });

  // Update local state when data loads
  useEffect(() => {
    if (data?.value) {
      setConfig({ ...DEFAULT_CONFIG, ...data.value });
    }
  }, [data]);

  const normalizeHexColor = (value: string | undefined, fallback: string) => {
    if (!value) return fallback;
    const normalized = value.trim();
    return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized : fallback;
  };

  const safeConfig = useMemo(
    () => ({
      ...config,
      primaryColor: normalizeHexColor(config.primaryColor, DEFAULT_CONFIG.primaryColor),
      backgroundColor: normalizeHexColor(config.backgroundColor, DEFAULT_CONFIG.backgroundColor),
      textColor: normalizeHexColor(config.textColor, DEFAULT_CONFIG.textColor),
    }),
    [config]
  );

  // API URL for embed - derive from current origin (crm.* -> api.*)
  const apiUrl = useMemo(() => {
    const origin = window.location.origin;
    // Replace 'crm.' with 'api.' or use VITE_API_URL if available
    if (origin.includes('crm.')) {
      return origin.replace('crm.', 'api.');
    }
    // For localhost, use the API port
    if (origin.includes('localhost')) {
      return origin.replace(':5173', ':8787');
    }
    return import.meta.env.VITE_API_URL || origin;
  }, []);

  // Embed URL - served from API worker
  const embedUrl = useMemo(() => {
    return `${apiUrl}/embed`;
  }, [apiUrl]);

  // Generate embed code (simple - uses server config)
  const embedCode = useMemo(() => {
    return `<iframe
  src="${embedUrl}"
  style="width: 100%; max-width: ${config.maxWidth}; height: ${config.height}px; border: none;"
  title="${config.title}"
></iframe>`;
  }, [embedUrl, config.maxWidth, config.height, config.title]);

  // Generate JS embed code with auto-resize
  const jsEmbedCode = useMemo(() => {
    return `<div id="leal-booking-widget"></div>
<script>
(function() {
  var container = document.getElementById('leal-booking-widget');
  var iframe = document.createElement('iframe');
  iframe.src = '${embedUrl}';
  iframe.style.cssText = 'width:100%;max-width:${config.maxWidth};border:none;';
  iframe.height = '${config.height}';
  container.appendChild(iframe);
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'leal-resize') {
      iframe.style.height = e.data.height + 'px';
    }
  });
})();
</script>`;
  }, [embedUrl, config.maxWidth, config.height]);

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    showSuccess('Codigo copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const updateConfig = (key: keyof WidgetConfig, value: string | boolean) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(safeConfig);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className={pageStyles.container}>
      {/* Header */}
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Widget de Reservas</h1>
          <p className={pageStyles.subtitle}>
            Personaliza el widget y copia el codigo para tu sitio web
          </p>
        </div>

        <Button size="sm" onClick={handleSave} disabled={!hasChanges || saveMutation.isPending}>
          {saveMutation.isPending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : null}
          Guardar
        </Button>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {/* Configuration Panel */}
        <div className="space-y-3">
          {/* Colors */}
          <div className={cardStyles.container}>
            <div className={cardStyles.header}>
              <div className="flex items-center gap-1.5">
                <Palette className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-xs font-medium text-gray-900">Colores</span>
              </div>
            </div>
            <div className={cardStyles.content}>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Color Principal
                  </label>
                  <div className="flex gap-1.5">
                    <input
                      type="color"
                      value={safeConfig.primaryColor}
                      onChange={(e) => updateConfig('primaryColor', e.target.value)}
                      className="h-8 w-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={config.primaryColor}
                      onChange={(e) => updateConfig('primaryColor', e.target.value)}
                      className="flex-1 font-mono text-xs h-8"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Fondo
                  </label>
                  <div className="flex gap-1.5">
                    <input
                      type="color"
                      value={safeConfig.backgroundColor}
                      onChange={(e) => updateConfig('backgroundColor', e.target.value)}
                      className="h-8 w-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={config.backgroundColor}
                      onChange={(e) => updateConfig('backgroundColor', e.target.value)}
                      className="flex-1 font-mono text-xs h-8"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Texto
                  </label>
                  <div className="flex gap-1.5">
                    <input
                      type="color"
                      value={safeConfig.textColor}
                      onChange={(e) => updateConfig('textColor', e.target.value)}
                      className="h-8 w-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={config.textColor}
                      onChange={(e) => updateConfig('textColor', e.target.value)}
                      className="flex-1 font-mono text-xs h-8"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className={cardStyles.container}>
            <div className={cardStyles.header}>
              <div className="flex items-center gap-1.5">
                <Type className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-xs font-medium text-gray-900">Textos</span>
              </div>
            </div>
            <div className={cardStyles.content}>
              <div className="space-y-2.5">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Titulo
                  </label>
                  <Input
                    value={config.title}
                    onChange={(e) => updateConfig('title', e.target.value)}
                    placeholder="Reservar Cita"
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Subtitulo
                  </label>
                  <Input
                    value={config.subtitle}
                    onChange={(e) => updateConfig('subtitle', e.target.value)}
                    placeholder="Seleccione el servicio..."
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Texto del Boton
                  </label>
                  <Input
                    value={config.buttonText}
                    onChange={(e) => updateConfig('buttonText', e.target.value)}
                    placeholder="Confirmar Reserva"
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Layout */}
          <div className={cardStyles.container}>
            <div className={cardStyles.header}>
              <div className="flex items-center gap-1.5">
                <Layout className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-xs font-medium text-gray-900">Diseno</span>
              </div>
            </div>
            <div className={cardStyles.content}>
              <div className="grid gap-2.5 sm:grid-cols-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Ancho
                  </label>
                  <Select
                    value={config.width}
                    onValueChange={(value) => updateConfig('width', value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100%">100% (responsive)</SelectItem>
                      <SelectItem value="500px">500px</SelectItem>
                      <SelectItem value="600px">600px</SelectItem>
                      <SelectItem value="400px">400px</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Ancho maximo
                  </label>
                  <Select
                    value={config.maxWidth}
                    onValueChange={(value) => updateConfig('maxWidth', value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100%">Sin limite</SelectItem>
                      <SelectItem value="400px">400px</SelectItem>
                      <SelectItem value="500px">500px</SelectItem>
                      <SelectItem value="600px">600px</SelectItem>
                      <SelectItem value="700px">700px</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Alto (px)
                  </label>
                  <Input
                    type="number"
                    value={config.height}
                    onChange={(e) => updateConfig('height', e.target.value)}
                    min="400"
                    max="1000"
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Bordes Redondeados (px)
                  </label>
                  <Input
                    type="number"
                    value={config.borderRadius}
                    onChange={(e) => updateConfig('borderRadius', e.target.value)}
                    min="0"
                    max="24"
                    className="h-8 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Estilo
                  </label>
                  <Select
                    value={config.variant}
                    onValueChange={(value) => updateConfig('variant', value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Plano (sin tarjeta)</SelectItem>
                      <SelectItem value="card">Tarjeta (con fondo/sombra)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Idioma
                  </label>
                  <Select
                    value={config.language}
                    onValueChange={(value) => updateConfig('language', value as 'es' | 'en')}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Espanol</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 pt-2.5 border-t border-gray-100">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config.showLogo}
                    onChange={(e) => updateConfig('showLogo', e.target.checked)}
                    className="rounded border-gray-300 h-3.5 w-3.5"
                  />
                  <span className="text-xs text-gray-700">Mostrar logo</span>
                </label>

                <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">
                      URL del logo (opcional)
                    </label>
                    <Input
                      value={config.logoUrl}
                      onChange={(e) => updateConfig('logoUrl', e.target.value)}
                      placeholder="https://.../logo.png"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">
                      Alto del logo (px)
                    </label>
                    <Input
                      type="number"
                      value={config.logoHeight}
                      onChange={(e) => updateConfig('logoHeight', e.target.value)}
                      min="16"
                      max="80"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>

                <div className="mt-2 grid gap-1.5">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!config.hideHeader}
                      onChange={(e) => updateConfig('hideHeader', !e.target.checked)}
                      className="rounded border-gray-300 h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-700">Mostrar cabecera del widget</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config.showProgress}
                      onChange={(e) => updateConfig('showProgress', e.target.checked)}
                      className="rounded border-gray-300 h-3.5 w-3.5"
                      disabled={config.hideHeader}
                    />
                    <span className="text-xs text-gray-700">Mostrar barra de progreso</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config.requestPhotosAfterVerification}
                      onChange={(e) => updateConfig('requestPhotosAfterVerification', e.target.checked)}
                      className="rounded border-gray-300 h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-700">Solicitar fotos tras verificar email</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview & Code Panel */}
        <div className="space-y-6">
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === 'preview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Eye className="h-4 w-4" />
              Vista Previa
            </button>
            <button
              onClick={() => setActiveTab('code')}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === 'code'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Code className="h-4 w-4" />
              Codigo
            </button>
          </div>

          {activeTab === 'preview' ? (
            <div className={cardStyles.container}>
              <div className={cardStyles.header}>
                <span className="text-xs font-medium text-gray-900">Vista Previa</span>
              </div>
              <div className={cardStyles.content}>
                <p className="text-xs text-gray-500 mb-3">
                  URL: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{embedUrl}</code>
                </p>
                <div
                  className="border rounded-lg overflow-hidden p-4"
                  style={{
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 50%, #ddd6fe 100%)',
                    minHeight: `${parseInt(config.height) + 32}px`,
                  }}
                >
                  <iframe
                    src={embedUrl}
                    style={{
                      width: config.width,
                      height: `${config.height}px`,
                      border: 'none',
                      borderRadius: `${config.borderRadius}px`,
                      maxWidth: '100%',
                      backgroundColor: config.backgroundColor,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
                    }}
                    title="Widget Preview"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Simple iframe Embed Code */}
              <div className={cardStyles.container}>
                <div className={`${cardStyles.header} flex items-start justify-between`}>
                  <div>
                    <p className="text-xs font-medium text-gray-900">Iframe simple</p>
                    <p className="text-[11px] text-gray-500">Altura fija, facil de implementar</p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopy(embedCode)}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <div className={cardStyles.content}>
                  <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto text-xs">
                    <code>{embedCode}</code>
                  </pre>
                </div>
              </div>

              {/* JS Embed Code with auto-resize */}
              <div className={cardStyles.container}>
                <div className={`${cardStyles.header} flex items-start justify-between`}>
                  <div>
                    <p className="text-xs font-medium text-gray-900">JavaScript (auto-resize)</p>
                    <p className="text-[11px] text-gray-500">Altura automatica segun contenido</p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopy(jsEmbedCode)}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <div className={cardStyles.content}>
                  <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto text-xs">
                    <code>{jsEmbedCode}</code>
                  </pre>
                </div>
              </div>

              {/* Direct Link */}
              <div className={cardStyles.container}>
                <div className={`${cardStyles.header} flex items-center justify-between`}>
                  <span className="text-xs font-medium text-gray-900">Enlace Directo</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopy(embedUrl)}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <div className={cardStyles.content}>
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <code className="text-sm text-gray-800 break-all">{embedUrl}</code>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
